import React, { useRef } from 'react';
import { Upload, X, Palette, Loader2, AlertCircle } from 'lucide-react';
import { Card } from '@/components/ui';
import { cn } from '@/lib/utils';
import type { DesignAsset, PlacementOption } from '../types';
import { MAX_FILE_SIZE } from '@/config/constants';

const ACCEPTED_FILES = '.png,.jpg,.jpeg,.svg,.webp,.pdf';

/** Derived so the card can never promise more than the server takes. */
const MAX_MB = Math.round(MAX_FILE_SIZE / 1024 / 1024);

interface DesignUploadCardProps {
  type: 'front' | 'back';
  design: DesignAsset | null;
  placement: string;
  placements: PlacementOption[];
  isUploading: boolean;
  uploadProgress: number;
  uploadError: string | null;
  onFileSelect: (file: File) => void;
  onOpenPlacementModal: () => void;
  onRemove: () => void;
}

/**
 * Card for uploading and managing one design side: a dashed drop zone
 * while empty, a progress card while uploading, the preview with the
 * placement action once uploaded.
 */
export function DesignUploadCard({
  type,
  design,
  placement,
  placements,
  isUploading,
  uploadProgress,
  uploadError,
  onFileSelect,
  onOpenPlacementModal,
  onRemove,
}: DesignUploadCardProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const placementInfo = placements.find((p) => p.id === placement);
  const sideLabel = type === 'front' ? 'Front' : 'Back';

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      onFileSelect(selectedFile);
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  const fileInput = (
    <input
      ref={inputRef}
      type="file"
      accept={ACCEPTED_FILES}
      onChange={handleChange}
      className="sr-only"
      aria-label={`${sideLabel} design file`}
    />
  );

  // Empty state - no design uploaded
  if (!design && !isUploading) {
    return (
      <div>
        {fileInput}
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className={cn(
            'flex min-h-[180px] w-full flex-col items-center justify-center rounded-section border-2 border-dashed p-6 text-center transition-[background-color,border-color] duration-[120ms] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
            uploadError
              ? 'border-danger bg-paper'
              : 'border-line bg-paper hover:border-primary hover:bg-paper-2'
          )}
        >
          {uploadError ? (
            <>
              <span
                className="mb-3 flex h-14 w-14 items-center justify-center rounded-section bg-paper-2 text-danger ring-1 ring-line"
                aria-hidden="true"
              >
                <AlertCircle className="h-6 w-6" />
              </span>
              <span className="font-semibold text-danger">Upload failed</span>
              <span className="mt-1 max-w-[200px] text-xs text-danger">
                {uploadError}
              </span>
              <span className="mt-2 text-xs text-mute">Click to try again</span>
            </>
          ) : (
            <>
              <span
                className="mb-3 flex h-14 w-14 items-center justify-center rounded-section bg-paper-2 text-mute ring-1 ring-line"
                aria-hidden="true"
              >
                <Upload className="h-6 w-6" />
              </span>
              <span className="font-semibold text-ink">{sideLabel} Design</span>
              <span className="mt-1 text-xs text-mute">
                PNG, JPG, SVG, WebP or PDF • max. {MAX_MB} MB
              </span>
            </>
          )}
        </button>
      </div>
    );
  }

  // Uploading state
  if (isUploading) {
    return (
      <Card
        variant="section"
        role="status"
        aria-live="polite"
        className="flex min-h-[180px] flex-col items-center justify-center text-center"
      >
        <span
          className="mb-3 flex h-14 w-14 items-center justify-center rounded-section bg-paper text-primary ring-1 ring-line"
          aria-hidden="true"
        >
          <Loader2 className="h-6 w-6 animate-spin" />
        </span>
        <p className="font-semibold text-ink">Uploading...</p>
        <div className="mt-3 w-full max-w-[150px]">
          <div className="h-2 overflow-hidden rounded-pill bg-paper-3">
            <div
              className="h-full bg-primary transition-[width] duration-300"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
          <p className="mt-1 text-xs text-mute">{uploadProgress}%</p>
        </div>
      </Card>
    );
  }

  // Design uploaded - show preview (design is set here, isUploading is false)
  const uploaded = design as DesignAsset;

  return (
    <Card
      variant="section"
      padding="none"
      className={cn(
        'overflow-hidden',
        uploadError
          ? 'ring-2 ring-danger'
          : placement
            ? 'ring-2 ring-primary'
            : 'ring-2 ring-warning'
      )}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-paper-3">
        <img
          src={uploaded.assetUrl}
          alt={`${sideLabel} design preview`}
          className="h-full w-full object-contain p-4"
        />

        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${sideLabel.toLowerCase()} design`}
          className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-control bg-ink text-paper transition-[filter] duration-[120ms] hover:brightness-[1.4] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>

        <span className="absolute left-2 top-2 rounded-chip bg-ink px-2 py-0.5 font-mono text-[11px] font-medium uppercase tracking-[0.08em] text-paper">
          {sideLabel}
        </span>
      </div>

      <div className="border-t border-line p-3">
        {/* Show error banner if re-upload failed */}
        {uploadError && (
          <div className="mb-2">
            {fileInput}
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="w-full rounded-control border border-danger bg-paper p-2 text-left transition-[background-color] duration-[120ms] hover:bg-paper-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <span className="flex items-center gap-2">
                <AlertCircle
                  className="h-4 w-4 shrink-0 text-danger"
                  aria-hidden="true"
                />
                <span className="min-w-0 flex-1">
                  <span className="block text-xs font-medium text-danger">
                    Re-upload failed
                  </span>
                  <span className="block truncate text-xs text-danger">
                    {uploadError}
                  </span>
                </span>
              </span>
              <span className="mt-1 block text-xs text-danger">
                Click to try again
              </span>
            </button>
          </div>
        )}

        <p
          className="mb-2 truncate text-xs text-mute"
          title={uploaded.fileName}
        >
          {uploaded.fileName}
        </p>

        {placement ? (
          <button
            type="button"
            onClick={onOpenPlacementModal}
            className="flex w-full items-center justify-between rounded-control bg-paper p-2.5 ring-1 ring-inset ring-line transition-[background-color] duration-[120ms] hover:bg-paper-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <span className="flex items-center gap-2">
              <span className="text-base" aria-hidden="true">
                {placementInfo?.icon}
              </span>
              <span className="text-sm font-medium text-ink">
                {placementInfo?.label}
              </span>
            </span>
            <span className="text-xs font-medium text-primary">Change</span>
          </button>
        ) : (
          <button
            type="button"
            onClick={onOpenPlacementModal}
            className="flex w-full items-center justify-center gap-2 rounded-control bg-warning p-2.5 text-ink transition-[filter] duration-[120ms] hover:brightness-[0.92] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <Palette className="h-4 w-4" aria-hidden="true" />
            <span className="text-sm font-semibold">Choose Position</span>
          </button>
        )}
      </div>
    </Card>
  );
}
